/**
 * PhotoLightbox Component
 * Full-screen modal photo viewer with navigation, download, and delete capabilities
 * Supports keyboard navigation (arrows, escape)
 *
 * @module components/gallery/PhotoLightbox
 */

import React, { useEffect, useCallback, useState } from 'react';
import { Photo, User, Album } from '@/types';
import { logger } from '@/utils/logger';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoLightboxProps {
  isOpen: boolean;
  photo: Photo | null;
  currentUser?: User | null;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onDelete?: (photoId: string) => void;
  onMove?: (photoId: string, albumId: string | null) => void;
  albums?: Album[];
  isDeleting?: boolean;
  isMoving?: boolean;
  hasNextPhoto?: boolean;
  hasPrevPhoto?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Full-screen photo lightbox modal with navigation and controls
 * Supports keyboard shortcuts (arrow keys, escape)
 * Shows photo metadata and action buttons for owners/admins
 *
 * @component
 * @example
 * <PhotoLightbox
 *   isOpen={isOpen}
 *   photo={currentPhoto}
 *   currentUser={user}
 *   onClose={handleClose}
 *   onNext={handleNext}
 *   onPrev={handlePrev}
 *   onDelete={handleDelete}
 *   hasNextPhoto={true}
 *   hasPrevPhoto={false}
 * />
 */
const PhotoLightbox: React.FC<PhotoLightboxProps> = ({
  isOpen,
  photo,
  currentUser,
  onClose,
  onNext,
  onPrev,
  onDelete,
  onMove,
  albums = [],
  isDeleting = false,
  isMoving = false,
  hasNextPhoto = false,
  hasPrevPhoto = false,
}) => {
  // Move dropdown state
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);

  // Debug logging
  useEffect(() => {
    if (isOpen && photo) {
      logger.debug(`[WALRUS] PhotoLightbox: opened`, {
        photoId: photo.id,
        filename: photo.filename,
      });
    }
  }, [isOpen, photo?.id, photo?.filename]);

  // Reset move dropdown when lightbox closes or photo changes
  useEffect(() => {
    setShowMoveDropdown(false);
  }, [isOpen, photo?.id]);

  // Check if current user can modify this photo (delete/move)
  const canModify =
    photo &&
    currentUser &&
    (currentUser.id === photo.uploadedBy || currentUser.role === 'admin');

  const canDelete = canModify;

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      logger.debug(`[WALRUS] PhotoLightbox: keyboard key pressed`, { key: e.key });

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          if (onNext && hasNextPhoto) {
            logger.debug(`[WALRUS] PhotoLightbox: navigating to next photo`);
            onNext();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (onPrev && hasPrevPhoto) {
            logger.debug(`[WALRUS] PhotoLightbox: navigating to previous photo`);
            onPrev();
          }
          break;
        case 'Escape':
          e.preventDefault();
          logger.debug(`[WALRUS] PhotoLightbox: closing via escape key`);
          onClose();
          break;
        default:
          break;
      }
    },
    [isOpen, hasNextPhoto, hasPrevPhoto, onNext, onPrev, onClose]
  );

  // Add keyboard event listener
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  /**
   * Handle download button click
   */
  const handleDownload = async () => {
    if (!photo || !photo.url) {
      logger.warn(`[WALRUS] PhotoLightbox: cannot download, no URL available`);
      return;
    }

    logger.debug(`[WALRUS] PhotoLightbox: downloading photo`, { photoId: photo.id });

    try {
      const response = await fetch(photo.url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photo.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.info(`[WALRUS] PhotoLightbox: photo downloaded`, { filename: photo.filename });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(`[WALRUS] PhotoLightbox: download failed`, { error: errorMessage });
    }
  };

  /**
   * Handle delete button click
   */
  const handleDeleteClick = () => {
    if (!photo || !onDelete || isDeleting) return;

    logger.debug(`[WALRUS] PhotoLightbox: delete clicked`, { photoId: photo.id });
    onDelete(photo.id);
  };

  /**
   * Handle move button click - toggle dropdown
   */
  const handleMoveToggle = () => {
    if (!photo || isMoving) return;
    logger.debug(`[WALRUS] PhotoLightbox: move dropdown toggled`, { photoId: photo.id });
    setShowMoveDropdown((prev) => !prev);
  };

  /**
   * Handle album selection from move dropdown
   */
  const handleMoveSelect = (targetAlbumId: string | null) => {
    if (!photo || !onMove || isMoving) return;

    logger.debug(`[WALRUS] PhotoLightbox: move selected`, {
      photoId: photo.id,
      targetAlbumId,
    });

    setShowMoveDropdown(false);
    onMove(photo.id, targetAlbumId);
  };

  if (!photo) return null;

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="flex flex-col gap-4">
        {/* Image container */}
        <div className="relative flex items-center justify-center bg-walrus-black rounded-lg overflow-hidden max-h-[600px]">
          <img
            src={photo.url || photo.thumbnailUrl}
            alt={photo.filename}
            className="max-h-[600px] max-w-full object-contain"
          />

          {/* Navigation arrows */}
          {hasPrevPhoto && onPrev && (
            <button
              onClick={onPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-lg bg-walrus-black bg-opacity-50 p-2 text-walrus-white transition-all hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Previous photo"
              type="button"
              title="Previous photo (or press left arrow)"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {hasNextPhoto && onNext && (
            <button
              onClick={onNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg bg-walrus-black bg-opacity-50 p-2 text-walrus-white transition-all hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Next photo"
              type="button"
              title="Next photo (or press right arrow)"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Photo metadata */}
        <div className="space-y-3 border-t border-walrus-gray pt-4">
          {/* Filename */}
          <div>
            <h3 className="text-sm font-semibold text-walrus-silver">Filename</h3>
            <p className="text-walrus-white break-all">{photo.filename}</p>
          </div>

          {/* Caption */}
          {photo.caption && (
            <div>
              <h3 className="text-sm font-semibold text-walrus-silver">Caption</h3>
              <p className="text-walrus-white">{photo.caption}</p>
            </div>
          )}

          {/* Uploader and date info */}
          <div className="grid grid-cols-2 gap-4">
            {photo.uploadedByName && (
              <div>
                <h3 className="text-sm font-semibold text-walrus-silver">Uploaded by</h3>
                <p className="text-walrus-white">{photo.uploadedByName}</p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-walrus-silver">Date</h3>
              <p className="text-walrus-white">{formatDate(photo.createdAt)}</p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-walrus-silver">File size</h3>
              <p className="text-walrus-white">{formatFileSize(photo.size)}</p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-walrus-silver">Type</h3>
              <p className="text-walrus-white">{photo.contentType}</p>
            </div>
          </div>

          {/* Tags if present */}
          {photo.tags && photo.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-walrus-silver mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {photo.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block rounded-full bg-walrus-gray px-3 py-1 text-sm text-walrus-white"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 border-t border-walrus-gray pt-4">
          <div className="flex-1 group relative" title="Download photo to your computer">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              className="w-full"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </Button>
          </div>

          {/* Move to Folder button - shown for owners and admins */}
          {canModify && onMove && albums.length > 0 && (
            <div className="flex-1 relative" title="Move photo to a different folder">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleMoveToggle}
                disabled={isMoving}
                loading={isMoving}
                className="w-full"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                  />
                </svg>
                {isMoving ? 'Moving...' : 'Move'}
              </Button>

              {/* Album selector dropdown */}
              {showMoveDropdown && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-walrus-white border border-walrus-gray rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                  {/* No Folder option */}
                  <button
                    onClick={() => handleMoveSelect(null)}
                    disabled={!photo.albumId}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      !photo.albumId
                        ? 'text-walrus-silver cursor-not-allowed bg-walrus-cream'
                        : 'text-walrus-black hover:bg-walrus-cream'
                    }`}
                    type="button"
                  >
                    No Folder
                    {!photo.albumId && (
                      <span className="ml-2 text-xs text-walrus-silver">(current)</span>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="border-t border-walrus-gray" />

                  {/* Album options */}
                  {albums.map((album) => {
                    const isCurrent = photo.albumId === album.id;
                    return (
                      <button
                        key={album.id}
                        onClick={() => handleMoveSelect(album.id)}
                        disabled={isCurrent}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          isCurrent
                            ? 'text-walrus-silver cursor-not-allowed bg-walrus-cream'
                            : 'text-walrus-black hover:bg-walrus-cream'
                        }`}
                        type="button"
                      >
                        {album.name}
                        {isCurrent && (
                          <span className="ml-2 text-xs text-walrus-silver">(current)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {canDelete && onDelete && (
            <div className="flex-1 group relative" title="Delete photo permanently">
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                loading={isDeleting}
                className="w-full"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Keyboard help text */}
        <div className="text-xs text-walrus-gray text-center">
          Use arrow keys to navigate, ESC to close
        </div>
      </div>
    </Modal>
  );
};

PhotoLightbox.displayName = 'PhotoLightbox';

export default PhotoLightbox;
